import { Heart } from "lucide-react";
import { useState } from "react";
import { useSavedTrips, useToggleSavedTrip } from "../../lib/queries";
import { useGuest } from "../shell/GuestShell";
import type { TripCard } from "../../types";

/**
 * The heart on a trip card or trip page (design turno 6): filled when the trip is in the
 * customer's collection. Optimistic; a failed call snaps back and says so in the title.
 */
export function SaveTripButton({ trip, placement, variant = "card", className = "" }: {
  trip: TripCard;
  placement: string;
  /** `card` floats over the photo; `glass` sits in the trip hero next to "Compartir"; `chip` is a labelled pill. */
  variant?: "card" | "glass" | "chip";
  className?: string;
}) {
  const { guest, askForAccount } = useGuest();
  const saved = useSavedTrips({ enabled: !guest });
  const toggle = useToggleSavedTrip();
  const [failed, setFailed] = useState(false);
  const isSaved = (saved.data ?? []).some((item) => item.tripId === trip.id);
  const label = isSaved ? "Quitar de guardados" : "Guardar viaje";

  const onClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (guest) { askForAccount("guardar este viaje"); return; }
    setFailed(false);
    try { await toggle(trip, !isSaved, placement); } catch { setFailed(true); }
  };

  const icon = <Heart size={variant === "card" ? 17 : 14} aria-hidden="true" className={isSaved ? "heart filled" : "heart"} />;
  if (variant === "glass") {
    return (
      <button type="button" className={`glass-button save-glass${isSaved ? " saved" : ""} ${className}`} onClick={onClick} aria-pressed={isSaved} title={failed ? "No pudimos guardar el cambio. Intenta de nuevo." : label}>
        {icon} {isSaved ? "Guardado" : "Guardar"}
      </button>
    );
  }
  if (variant === "chip") {
    return (
      <button type="button" className={`chip small save-chip${isSaved ? " active" : ""} ${className}`} onClick={onClick} aria-pressed={isSaved} title={label}>
        {icon} {isSaved ? "Guardado" : "Guardar"}
      </button>
    );
  }
  return (
    <button type="button" className={`save-heart${isSaved ? " saved" : ""}${failed ? " failed" : ""} ${className}`} onClick={onClick} aria-pressed={isSaved} aria-label={label} title={failed ? "No pudimos guardar el cambio. Intenta de nuevo." : label}>
      {icon}
    </button>
  );
}
